<template>
  <div class="bg-light min-vh-100">
    <main class="container">
      <div class="row justify-content-center">
        <div class="col-lg-8">
          <div class="card shadow-sm">
            <div class="card-body">
              <h1 class="h4 mb-2">{{ isEditMode ? 'Edit Device Model' : 'New Device Model' }}</h1>
              <p class="help mb-3">{{ isEditMode ? 'Update this device model.' : 'Add a new device model to the catalog.' }}</p>

              <div v-if="fetchLoading" class="text-center py-5">
                <div class="spinner-border" role="status"></div>
              </div>

              <form v-else @submit.prevent="handleSubmit">
                <!-- Row: Make + Model Name -->
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">Make</label>
                    <input v-model="form.make" type="text" class="form-control" required placeholder="e.g. Lenovo" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Model Name</label>
                    <input v-model="form.model_name" type="text" class="form-control" required :placeholder="hints.model_name" />
                  </div>
                </div>

                <!-- Row: Common Name + Device Type -->
                <div class="row g-3 mt-1">
                  <div class="col-md-6">
                    <label class="form-label">Common Name</label>
                    <input v-model="form.common_name" type="text" class="form-control" :placeholder="hints.common_name" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Device Type</label>
                    <select v-model="form.device_type" class="form-select" required>
                      <option value="Laptop">Laptop</option>
                      <option value="Desktop">Desktop</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <!-- Row: CPU + RAM -->
                <div class="row g-3 mt-1">
                  <div class="col-md-6">
                    <label class="form-label">CPU</label>
                    <input v-model="form.cpu" type="text" class="form-control" :placeholder="hints.cpu" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">RAM</label>
                    <input v-model="form.ram" type="text" class="form-control" :placeholder="hints.ram" />
                  </div>
                </div>

                <!-- Row: Storage + GPU -->
                <div class="row g-3 mt-1">
                  <div class="col-md-6">
                    <label class="form-label">Storage</label>
                    <input v-model="form.storage" type="text" class="form-control" :placeholder="hints.storage" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">GPU</label>
                    <input v-model="form.gpu" type="text" class="form-control" :placeholder="hints.gpu" />
                  </div>
                </div>

                <!-- Row: Screen Size + Display Resolution (Laptop/Other) OR Form Factor hints (Desktop) -->
                <div class="row g-3 mt-1">
                  <div class="col-md-6" v-if="isLaptop || isOther">
                    <label class="form-label">Screen Size</label>
                    <input v-model="form.screen_size" type="text" class="form-control" :placeholder="hints.screen_size" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Display Resolution</label>
                    <input v-model="form.display_resolution" type="text" class="form-control" :placeholder="hints.display_resolution" />
                  </div>
                  <div class="col-md-6" v-if="isDesktop">
                    <label class="form-label">Screen Size <span class="text-muted small">(if AIO or included monitor)</span></label>
                    <input v-model="form.screen_size" type="text" class="form-control" placeholder="e.g. 27 inch (if applicable)" />
                  </div>
                </div>

                <!-- Row: Connectivity + Weight -->
                <div class="row g-3 mt-1">
                  <div class="col-md-8">
                    <label class="form-label">Connectivity</label>
                    <textarea v-model="form.connectivity" class="form-control" rows="2" :placeholder="hints.connectivity"></textarea>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">{{ isDesktop ? 'Weight / Dimensions' : 'Weight' }}</label>
                    <input v-model="form.weight" type="text" class="form-control" :placeholder="hints.weight" />
                  </div>
                </div>

                <!-- Row: Battery Info (Laptop) OR Power Supply (Desktop) -->
                <div class="mt-3" v-if="isLaptop">
                  <label class="form-label">Battery Info</label>
                  <textarea v-model="form.battery_info" class="form-control" rows="2" :placeholder="hints.battery_info"></textarea>
                </div>
                <div class="mt-3" v-if="isDesktop">
                  <label class="form-label">Power Supply / Form Factor</label>
                  <textarea v-model="form.battery_info" class="form-control" rows="2" :placeholder="hints.battery_info"></textarea>
                </div>

                <hr class="my-4" />
                <h6 class="mb-3">Listing Details <span class="text-muted small">— These fields populate your eBay listing</span></h6>

                <div class="mb-3">
                  <label class="form-label">Ports</label>
                  <textarea v-model="form.ports" class="form-control" rows="4" :placeholder="hints.ports"></textarea>
                  <small class="text-muted">One per line. Be specific: count each port, include version numbers.</small>
                </div>

                <div class="mb-3">
                  <label class="form-label">Features</label>
                  <textarea v-model="form.features" class="form-control" rows="4" :placeholder="hints.features"></textarea>
                  <small class="text-muted">One per line. Include durability ratings, security features, display capabilities.</small>
                </div>

                <div class="mb-3">
                  <label class="form-label">What's Included</label>
                  <textarea v-model="form.whats_included" class="form-control" rows="2" :placeholder="hints.whats_included"></textarea>
                  <small class="text-muted">One per line. Include charger wattage, cables, peripherals.</small>
                </div>

                <div class="mb-3">
                  <label class="form-label">Listing Notes</label>
                  <textarea v-model="form.listing_notes" class="form-control" rows="2" placeholder="Fine print shown at bottom of eBay listing"></textarea>
                </div>

                <!-- Notes -->
                <div class="mt-3">
                  <label class="form-label">Internal Notes <span class="text-muted small">(not shown in listings)</span></label>
                  <textarea v-model="form.notes" class="form-control" rows="2" placeholder="Private notes for your team"></textarea>
                </div>

                <!-- Collapsible Advanced -->
                <div class="mt-4">
                  <button type="button" class="btn btn-outline-secondary btn-sm" @click="showAdvanced = !showAdvanced">
                    {{ showAdvanced ? 'Hide' : 'Show' }} Advanced
                  </button>
                </div>

                <div v-if="showAdvanced" class="mt-3 border rounded p-3 bg-white">
                  <div class="mb-0">
                    <label class="form-label">Custom HTML Template <span class="text-muted small">(overrides auto-generated listing)</span></label>
                    <textarea v-model="form.listing_template_html" class="form-control font-monospace" rows="4" placeholder="Use placeholders: {{cpu}}, {{gpu}}, {{ram}}, {{storage}}, {{screen_size}}, {{make}}, {{model_name}}, {{common_name}}, {{ports}}, {{features}}, {{whats_included}}, {{listing_notes}}, {{installed_os}}, {{cosmetic_grade}}, {{cosmetic_notes}}, {{display_resolution}}, {{connectivity}}, {{battery_info}}, {{weight}}"></textarea>
                  </div>
                </div>

                <div v-if="error" class="alert alert-danger py-2 mt-3">{{ error }}</div>

                <div class="d-flex gap-2 mt-4">
                  <button type="button" class="btn btn-outline-secondary" @click="$router.push({ name: 'DeviceModelsList' })">Cancel</button>
                  <button type="submit" class="btn btn-primary" :disabled="loading">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                    {{ isEditMode ? 'Save Changes' : 'Create Model' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { APIService } from "../http/APIService";

const LAPTOP_HINTS = {
  model_name: "e.g. ThinkPad P15 Gen 1",
  common_name: "e.g. P15 Gen 1",
  cpu: "e.g. Intel Core i7-10850H (6-Core, 2.7GHz)",
  ram: "e.g. 32GB DDR4-3200",
  storage: "e.g. 1TB NVMe PCIe Gen3 SSD",
  gpu: "e.g. NVIDIA Quadro T1000 (4GB GDDR6)",
  screen_size: "e.g. 15.6 inch",
  display_resolution: "e.g. 1920x1080 IPS, 300 nit",
  connectivity: "e.g. WiFi 6 (AX201), Bluetooth 5.1, optional 4G LTE",
  weight: "e.g. 5.5 lbs",
  battery_info: "e.g. 94Wh integrated Li-Polymer, up to 10hr battery life",
  ports: "One per line — be specific!\ne.g.\n2x USB-A 3.2 Gen 1\n1x USB-C 3.2 Gen 2\n2x Thunderbolt 3 (USB-C)\n1x HDMI 2.0\n1x RJ-45 Ethernet\n1x SD Card Reader\n1x 3.5mm Audio Combo\n1x Smart Card Reader",
  features: "One per line\ne.g.\nMIL-STD-810G Durability Tested\nSupports up to 5 external displays\nFingerprint Reader\nIR Camera with Windows Hello\nBacklit Keyboard\nTPM 2.0 Security Chip\nRapid Charge (80% in 1 hour)",
  whats_included: "e.g.\nLaptop\n170W Lenovo AC Power Adapter",
};

const DESKTOP_HINTS = {
  model_name: "e.g. OptiPlex 7090 SFF",
  common_name: "e.g. OptiPlex 7090",
  cpu: "e.g. Intel Core i7-11700 (8-Core, 2.5GHz)",
  ram: "e.g. 32GB DDR4-3200 (2 of 4 DIMM slots used)",
  storage: "e.g. 512GB NVMe M.2 SSD + 1TB 3.5in HDD",
  gpu: "e.g. Intel UHD 750 (integrated) or NVIDIA T400 (2GB)",
  screen_size: "",
  display_resolution: "e.g. Supports up to 4K@60Hz (DP), 4K@30Hz (HDMI)",
  connectivity: "e.g. Intel I219-LM Gigabit Ethernet, WiFi 6 (optional), Bluetooth 5.2",
  weight: "e.g. 11.6 lbs, SFF (11.4 x 3.65 x 11.5 in)",
  battery_info: "e.g. 200W internal PSU, SFF form factor, tool-less access chassis",
  ports: "One per line — be specific!\ne.g.\nFront:\n2x USB-A 3.2 Gen 1\n1x USB-C 3.2 Gen 2\n1x 3.5mm Audio Combo\nRear:\n4x USB-A 3.2 Gen 1\n1x USB-C 3.2 Gen 2\n2x DisplayPort 1.4\n1x HDMI 2.0\n1x RJ-45 Gigabit Ethernet\n1x Serial (optional)",
  features: "One per line\ne.g.\nTool-less chassis for easy upgrades\n4 DIMM slots (max 128GB RAM)\n1x M.2 PCIe slot + 1x 2.5in bay + 1x 3.5in bay\nTPM 2.0 Security Chip\nSupports up to 3 independent displays\nvPro remote management capable\nENERGY STAR certified",
  whats_included: "e.g.\nDesktop Tower/SFF\nAC Power Cable",
};

const OTHER_HINTS = {
  model_name: "e.g. Surface Pro 9",
  common_name: "e.g. Surface Pro 9",
  cpu: "e.g. Intel Core i5-1235U",
  ram: "e.g. 8GB LPDDR5",
  storage: "e.g. 256GB NVMe SSD (removable)",
  gpu: "e.g. Intel Iris Xe Graphics",
  screen_size: "e.g. 13 inch",
  display_resolution: "e.g. 2880x1920 PixelSense, 120Hz",
  connectivity: "e.g. WiFi 6E, Bluetooth 5.1",
  weight: "e.g. 1.94 lbs (tablet only)",
  battery_info: "e.g. Up to 15.5 hours typical usage",
  ports: "One per line\ne.g.\n2x USB-C Thunderbolt 4\n1x Surface Connect\n1x 3.5mm Audio",
  features: "One per line\ne.g.\nDetachable keyboard (sold separately)\n10-point multi-touch display\nWindows Hello face authentication\nKickstand with continuous positioning",
  whats_included: "e.g.\nTablet\n65W Surface Power Adapter",
};

export default {
  name: "CreateDeviceModel",
  data() {
    return {
      form: {
        make: "",
        model_name: "",
        common_name: "",
        device_type: "Laptop",
        cpu: "",
        ram: "",
        storage: "",
        screen_size: "",
        gpu: "",
        display_resolution: "",
        connectivity: "",
        weight: "",
        battery_info: "",
        ports: "",
        features: "",
        whats_included: "",
        listing_notes: "",
        listing_template_html: "",
        notes: "",
      },
      showAdvanced: false,
      loading: false,
      fetchLoading: false,
      error: null,
      api: new APIService(),
    };
  },
  computed: {
    isEditMode() {
      return !!this.$route.params.id;
    },
    isLaptop() {
      return this.form.device_type === "Laptop";
    },
    isDesktop() {
      return this.form.device_type === "Desktop";
    },
    isOther() {
      return this.form.device_type === "Other";
    },
    hints() {
      if (this.isDesktop) return DESKTOP_HINTS;
      if (this.isOther) return OTHER_HINTS;
      return LAPTOP_HINTS;
    },
  },
  created() {
    if (this.isEditMode) {
      this.fetchModel();
    }
  },
  methods: {
    async fetchModel() {
      this.fetchLoading = true;
      try {
        const accessToken = localStorage.getItem("accessToken");
        const res = await this.api.getDeviceModel(this.$route.params.id, accessToken);
        const dm = res.data;
        for (const key of Object.keys(this.form)) {
          if (dm[key] !== undefined && dm[key] !== null) {
            this.form[key] = dm[key];
          }
        }
        if (dm.listing_template_html) {
          this.showAdvanced = true;
        }
      } catch {
        this.error = "Unable to load device model.";
      } finally {
        this.fetchLoading = false;
      }
    },
    async handleSubmit() {
      this.error = null;
      this.loading = true;
      try {
        const accessToken = localStorage.getItem("accessToken");
        if (this.isEditMode) {
          await this.api.updateDeviceModel(this.$route.params.id, this.form, accessToken);
        } else {
          await this.api.createDeviceModel(this.form, accessToken);
        }
        this.$router.push({ name: "DeviceModelsList" });
      } catch (err) {
        console.error("Save device model failed:", err);
        this.error = this.isEditMode ? "Unable to update device model." : "Unable to create device model.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
